import { useEffect, useState } from 'react';
import { isReactNativeWebView } from 'utils/webAuthn';
import {
  clearWebViewSession,
  parseWebViewSessionMessage,
  readWebViewSessionFromBridge,
  setWebViewSession,
} from 'utils/webViewSession';

export const WEBVIEW_SESSION_WAIT_MS = 3000;

/**
 * Resolve the session the RN DigitalSignatureScreen injects.
 * - 'ready'   outside the RN WebView, or once a session is stored
 * - 'pending' inside the RN WebView while waiting for the global or the message
 * - 'none'    RN did not deliver one within WEBVIEW_SESSION_WAIT_MS (proceed anonymously)
 */
export default function useWebViewSession() {
  const [status, setStatus] = useState(() => {
    if (!isReactNativeWebView()) {
      return 'ready';
    }
    return setWebViewSession(readWebViewSessionFromBridge()) ? 'ready' : 'pending';
  });

  useEffect(() => {
    if (status !== 'pending') {
      return undefined;
    }

    const onMessage = event => {
      const session = parseWebViewSessionMessage(event.data);
      if (session && setWebViewSession(session)) {
        setStatus('ready');
      }
    };

    window.addEventListener('message', onMessage);
    const timer = setTimeout(
      () => setStatus(current => (current === 'pending' ? 'none' : current)),
      WEBVIEW_SESSION_WAIT_MS,
    );

    return () => {
      window.removeEventListener('message', onMessage);
      clearTimeout(timer);
    };
  }, [status]);

  useEffect(() => () => clearWebViewSession(), []);

  return status;
}
